import * as Common from '@piccolohealth/pbs-common';
import { ChakraV3, Spin, useRequiredParams } from '@piccolohealth/ui';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PiccoloError } from '../../components/generic/PiccoloError';
import { TabsRouter } from '../../components/generic/TabsRouter';
import { ContentLayout } from '../../components/layouts/ContentLayout';
import { useParticipantMinimalQuery } from '../../graphql/hooks/useParticipantQuery';
import { useAppContext } from '../../hooks/useAppContext';
import { ParticipantBehavioursBadge } from '../behaviours/components/ParticipantBehavioursBadge';
import { ParticipantBehaviours } from '../behaviours/ParticipantBehaviours';
import { ParticipantDashboard } from '../dashboard/ParticipantDashboard';
import { ParticipantFilesAndDocuments } from '../files/ParticipantFilesAndDocuments';
import { ParticipantNotes } from '../notes/ParticipantNotes';
import { ParticipantNotesBadge } from '../notes/ParticipantNotesBadge';
import { ParticipantDataBadge } from '../participantData/components/ParticipantDataBadge';
import { ParticipantData } from '../participantData/ParticipantData';
import { ParticipantDocumentsBadge } from '../participantDocument/ParticipantDocumentsBadge';
import { ParticipantTeam } from '../participantTeam/ParticipantTeam';
import { ParticipantTeamBadge } from '../participantTeam/ParticipantTeamBadge';
import { ParticipantTimelineControl } from '../participantTimeline/ParticipantTimelineControl';
import { ParticipantRestrictivePracticesBadge } from '../restrictivePractices/components/ParticipantRestrictivePracticesBadge';
import { ParticipantRestrictivePractices } from '../restrictivePractices/ParticipantRestrictivePractices';
import { ParticipantActionMenu } from './ParticipantActionMenu';
import { ParticipantDisabledStatus } from './ParticipantDisabledStatus';
import { ParticipantVersionStatus } from './ParticipantVersionStatus';

export const Participant = () => {
  const { participantId } = useRequiredParams<{ participantId: string }>();

  const { organization } = useAppContext();
  const navigate = useNavigate();

  const { data, error, isLoading } = useParticipantMinimalQuery({
    organizationId: organization.id,
    participantId,
  });

  const participant = data?.organization?.participant;

  if (isLoading) {
    return <Spin />;
  }

  if (error) {
    return <PiccoloError error={error} />;
  }

  if (!participant) {
    return (
      <PiccoloError
        error={
          new Common.PiccoloError({
            type: 'ParticipantNotFound',
            message: 'Participant not found',
          })
        }
      />
    );
  }

  const isDisabled = Common.isParticipantDisabled(participant.status);

  const routes = [
    'dashboard',
    'team',
    'documents',
    'behaviours',
    'restrictivePractices',
    'data',
    'notes',
  ];

  return (
    <ContentLayout
      headerContent={
        <ChakraV3.HStack>
          <ChakraV3.Heading size='3xl' fontWeight='bold'>
            {Common.renderParticipantName(participant)}
          </ChakraV3.Heading>
          <ChakraV3.Spacer />
          <ParticipantTimelineControl participantId={participant.id} />
          <ParticipantActionMenu
            participantId={participant.id}
            participantName={Common.renderParticipantName(participant)}
            isDisabled={isDisabled}
            size='sm'
          />
        </ChakraV3.HStack>
      }
    >
      <ChakraV3.Stack h='full'>
        <ChakraV3.Stack gap={6} h='full' w='full'>
          <TabsRouter
            routes={routes}
            defaultValue={routes[0]}
            variant='enclosed'
            size='sm'
            w='full'
            h='full'
            display='flex'
            flexDir='column'
            lazyMount
            unmountOnExit
          >
            <ChakraV3.Stack px={6} gap={4}>
              <ChakraV3.Stack>
                <ParticipantDisabledStatus status={participant.status} />
                {!participant.isOnLatestFields && (
                  <ParticipantVersionStatus participantId={participant.id} />
                )}
              </ChakraV3.Stack>

              <ChakraV3.Tabs.List w='fit-content'>
                <ChakraV3.Tabs.Trigger value='dashboard'>Dashboard</ChakraV3.Tabs.Trigger>
                <ChakraV3.Tabs.Trigger value='team'>
                  <ChakraV3.HStack>
                    Team
                    <ParticipantTeamBadge participantId={participant.id} />
                  </ChakraV3.HStack>
                </ChakraV3.Tabs.Trigger>
                {/* Workaround to navigate to an nested route using TabsRouter.
                Chakra doesnt let you customise the onClick hamdlers nicely. */}
                <ChakraV3.Tabs.Trigger
                  value='documents'
                  onClick={() =>
                    navigate(
                      `/organizations/${organization.id}/participants/${participantId}/documents`,
                    )
                  }
                >
                  <ChakraV3.HStack>
                    Documents
                    <ParticipantDocumentsBadge participantId={participant.id} />
                  </ChakraV3.HStack>
                </ChakraV3.Tabs.Trigger>
                <ChakraV3.Tabs.Trigger value='behaviours'>
                  <ChakraV3.HStack>
                    Behaviours
                    <ParticipantBehavioursBadge participantId={participant.id} />
                  </ChakraV3.HStack>
                </ChakraV3.Tabs.Trigger>

                <ChakraV3.Tabs.Trigger value='restrictivePractices'>
                  <ChakraV3.HStack>
                    Restrictive Practices
                    <ParticipantRestrictivePracticesBadge participantId={participant.id} />
                  </ChakraV3.HStack>
                </ChakraV3.Tabs.Trigger>
                <ChakraV3.Tabs.Trigger value='data'>
                  <ChakraV3.HStack>
                    Data
                    <ParticipantDataBadge participantId={participant.id} />
                  </ChakraV3.HStack>
                </ChakraV3.Tabs.Trigger>
                <ChakraV3.Tabs.Trigger
                  value='notes'
                  onClick={() =>
                    navigate(
                      `/organizations/${organization.id}/participants/${participantId}/notes`,
                    )
                  }
                >
                  <ChakraV3.HStack>
                    Notes
                    <ParticipantNotesBadge participantId={participant.id} />
                  </ChakraV3.HStack>
                </ChakraV3.Tabs.Trigger>
              </ChakraV3.Tabs.List>
            </ChakraV3.Stack>

            <ChakraV3.Tabs.ContentGroup overflowY='auto' h='full' w='full'>
              <ChakraV3.Tabs.Content h='full' value='dashboard'>
                <ParticipantDashboard participantId={participant.id} isDisabled={isDisabled} />
              </ChakraV3.Tabs.Content>
              <ChakraV3.Tabs.Content h='full' value='team'>
                <ParticipantTeam participantId={participant.id} isDisabled={isDisabled} />
              </ChakraV3.Tabs.Content>
              <ChakraV3.Tabs.Content h='full' value='documents'>
                <ParticipantFilesAndDocuments
                  participantId={participant.id}
                  isDisabled={isDisabled}
                />
              </ChakraV3.Tabs.Content>
              <ChakraV3.Tabs.Content h='full' value='behaviours'>
                <ParticipantBehaviours participantId={participant.id} isDisabled={isDisabled} />
              </ChakraV3.Tabs.Content>
              <ChakraV3.Tabs.Content h='full' value='restrictivePractices'>
                <ParticipantRestrictivePractices
                  participantId={participant.id}
                  isDisabled={isDisabled}
                />
              </ChakraV3.Tabs.Content>
              <ChakraV3.Tabs.Content h='full' value='data'>
                <ParticipantData participantId={participant.id} isDisabled={isDisabled} />
              </ChakraV3.Tabs.Content>
              <ChakraV3.Tabs.Content value='notes'>
                <ParticipantNotes participantId={participant.id} isDisabled={isDisabled} />
              </ChakraV3.Tabs.Content>
            </ChakraV3.Tabs.ContentGroup>
          </TabsRouter>
        </ChakraV3.Stack>
      </ChakraV3.Stack>
    </ContentLayout>
  );
};
